<template>
  <div v-if="user" class="bg-card rounded-lg border border-border overflow-hidden">
    <div class="p-4 border-b flex items-center justify-between flex-wrap gap-3">
      <h2 class="text-lg font-semibold flex items-center gap-2">
        Users
        <span class="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-normal">
          {{ user.created_team_users ? user.created_team_users.length : 0 }}
        </span>
      </h2>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-foreground hover:bg-foreground/90 text-background h-9 px-4"
        :disabled="!canAddUser"
        @click="openAddModal"
      >
        Add User
      </button>
    </div>
    <div v-if="user.role === 'Agency' && (!user.teams || user.teams.length === 0)" class="p-6 text-center text-sm text-muted-foreground">
      <p>Add businesses (customers) first on the Leads page. Then you can add users here and assign them to specific businesses.</p>
    </div>
    <div v-else class="relative w-full overflow-auto">
      <table class="w-full caption-bottom text-sm">
        <thead class="[&_tr]:border-b">
          <tr class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50 bg-muted/50">
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              First Name
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Last Name
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Email
            </th>
            <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="[&_tr:last-child]:border-0">
          <tr
            v-for="u in user.created_team_users"
            :key="u.id"
            class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50"
          >
            <td class="p-4 align-middle">{{ u.first_name }}</td>
            <td class="p-4 align-middle">{{ u.last_name }}</td>
            <td class="p-4 align-middle">{{ u.email }}</td>
            <td class="p-4 align-middle text-right">
              <div class="flex items-center justify-end gap-2 flex-wrap">
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input hover:text-accent-foreground rounded-md h-7 px-3 bg-foreground text-background hover:bg-foreground/90"
                  @click="editTeamUser(u)"
                >
                  Edit
                </button>
                <button
                  v-if="user.role === 'Agency' || user.role === 'Admin'"
                  type="button"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input hover:bg-accent rounded-md h-7 px-3"
                  @click="openChangePasswordModal(u)"
                >
                  Change password
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md h-7 px-3"
                  @click="askToDeleteTeamUser(u)"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <b-modal v-model="showChangePasswordModal" title="Change password" @close="resetChangePasswordForm">
      <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="changePasswordFormRef">
        <form @submit.prevent="handleSubmit(submitChangePassword)">
          <p v-if="userToChangePassword" class="text-muted small mb-3">Set a new password for {{ userToChangePassword.first_name }} {{ userToChangePassword.last_name }} ({{ userToChangePassword.email }}).</p>
          <b-form-group label="New password">
            <input
              v-model="changePasswordForm.password"
              type="password"
              class="form-control"
              placeholder="New password"
              required
            />
          </b-form-group>
          <b-form-group label="Confirm password">
            <input
              v-model="changePasswordForm.passwordConfirmation"
              type="password"
              class="form-control"
              placeholder="Confirm password"
              required
            />
          </b-form-group>
          <div class="d-flex justify-content-end gap-2">
            <b-button variant="light" @click="showChangePasswordModal = false">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="loadingChangePassword || (invalid && touched)">
              <b-spinner v-if="loadingChangePassword" small />
              <span v-else>Change password</span>
            </b-button>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>
    <b-modal v-model="showDeleteModal" title="Delete User">
      <p v-if="selectedUser"
      >Are you sure you want to delete user "{{
          selectedUser.first_name + ' ' + selectedUser.last_name
        }}"?</p
      >
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false"
        >Cancel</b-button
        >
        <b-button variant="danger" @click="deleteUser">
          <b-spinner v-if="loadingRemove" small />
          <span>Delete</span>
        </b-button>
      </template>
    </b-modal>
    <b-modal v-model="addUser" :hide-footer="true" title="Team User" @close="resetNewUser">
      <ValidationObserver
          v-slot="{ handleSubmit, invalid, touched }"
          ref="newUserForm"
      >
        <form
            class="new-user-form"
            @submit.prevent="handleSubmit(addNewUser)"
        >
          <div class="row">
            <div class="col-12">
              <TextInput
                  v-model="newUser.first_name"
                  name="first_name"
                  rules="required"
                  placeholder="First Name"
              />
            </div>
            <div class="col-12">
              <TextInput
                  v-model="newUser.last_name"
                  name="last_name"
                  rules="required"
                  placeholder="Last Name"
              />
            </div>
            <div class="col-12">
              <TextInput
                  v-model="newUser.email"
                  name="email"
                  rules="required|email"
                  placeholder="Email"
              />
            </div>
            <div class="col-12">
              <label for="teams">{{ user.role === 'Agency' ? 'Assign to businesses' : 'Account Access' }}</label>
              <p v-if="user.role === 'Agency'" class="text-muted small mb-1">Select which businesses this user can access. A password will be emailed to new users.</p>
              <button v-if="user.role !== 'Business'" type="button" class="btn btn-link" @click="checkAllTeams">Check All</button>
              <button v-if="user.role !== 'Business'" type="button" class="btn btn-link" @click="uncheckAllTeams">Uncheck All</button>
              <!-- Dropdown with checkboxes for teams -->
              <b-form-group id="teams">
                <div class="scrollable-checkbox-group">
                  <b-form-checkbox-group
                      v-model="newUser.selectedTeams"
                      :options="teamOptions"
                      name="teams"
                      stacked
                      class="small-checkboxes"
                  />
                </div>
              </b-form-group>
            </div>
          </div>
          <b-form-group class="text-right">
            <b-button
                variant="primary"
                class="btn-dark-blue"
                :disabled="loadingUser || (invalid && touched)"
                type="submit"
            >
              <b-spinner v-if="loadingUser" small />
              <span v-else>Submit</span>
            </b-button>
          </b-form-group>
        </form>
      </ValidationObserver>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { businessLimitForUser } from '@utils/businessLimitsFeature'

export default {
  data() {
    return {
      selectedUser: null,
      loadingRemove: false,
      showDeleteModal: false,
      addUser: false,
      loadingUser: false,
      showChangePasswordModal: false,
      userToChangePassword: null,
      loadingChangePassword: false,
changePasswordForm: {
          password: '',
          passwordConfirmation: ''
        },
      user: {
        agency: {},
      },
      newUser: {
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        agency: null,
        selectedTeams: []
      },
    }
  },
  computed: {
    teamOptions() {
      if (!this.user.teams || !this.user.teams.length) return [];
      return this.user.teams.map(team => ({
        value: team.id,
        text: team.name
      }));
    },
    selectedTeam(){
      return this.user ? this.user.teams?.find(t => this.user?.role === 'Agency' ? t?.team_users?.filter(t2 => t2.created_by === this.user?.id) : t.owner_id === this.user?.id) : null;
    },
    selectedTeamUserCount() {
      return this.selectedTeam ? this.selectedTeam.team_users.length : 0;
    },
    canAddUser() {
      if (this.user.role === 'Agency' && (!this.user.teams || this.user.teams.length === 0)) return false;
      const teamLimit = businessLimitForUser(this.user, 'team_user_limit');
      if (teamLimit == null) {
        return true;
      }
      return (this.user.created_team_users ? this.user.created_team_users.length : 0) < teamLimit;
    },
  },

  mounted() {
    this.user = Vue.util.extend({}, this.$store.getters['auth/user']);
    this.newUser.role = this.user.role
    this.newUser.agency = this.user.team_agency;
    if(this.teamOptions.length === 1 && this.user.role === 'Business'){
      this.newUser.selectedTeams = [this.teamOptions[0].value]
    }
  },
  methods: {
    openAddModal(){
      if (this.user.role === 'Agency' && (!this.user.teams || this.user.teams.length === 0)) {
        Vue.$toast.open({
          message: "Add businesses (customers) first, then you can add users and assign them to specific businesses.",
          type: 'error',
          duration: 5000,
        });
        return;
      }
      const teamLimit = businessLimitForUser(this.user, 'team_user_limit');
      if (teamLimit != null && (this.user.created_team_users ? this.user.created_team_users.length : 0) >= teamLimit) {
        Vue.$toast.open({
          message: "Sorry, but you have reached your team user limit.",
          type: 'error',
          duration: 5000,
        });
        return;
      }
      this.addUser = true;
    },
    deleteUser() {
      if (!this.selectedUser) return;

      const team = this.user.teams?.find((t) =>
        t.team_users?.some((u) => u.id === this.selectedUser.id)
      );

      if (!team) {
        Vue.$toast.open({
          message: 'User not found in any team',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      this.loadingRemove = true;
      this.$store
          .dispatch('user/removeTeamUser', {
            userId: this.selectedUser.id,
            teamId: team.id
          })
          .then((res) => {
            this.user.teams = res.data.user.teams;
            this.user.created_team_users = res.data.user.created_team_users;
            this.showDeleteModal = false
            this.selectedUser = null
            this.loadingRemove = false;
          })
          .catch(() => {
            this.loadingRemove = false;
          });
    },
    askToDeleteTeamUser(user){
      this.selectedUser = user;
      this.showDeleteModal = true;
    },
    editTeamUser(teamUser){
      this.newUser.first_name = teamUser.first_name;
      this.newUser.last_name = teamUser.last_name;
      this.newUser.email = teamUser.email;
      this.newUser.selectedTeams = this.user.teams.filter(team => team.team_users.some(user => user.id === teamUser.id)).map(team => team.id)
      this.addUser = true;
    },
    checkAllTeams() {
      // Select all teams in the checkbox group
      this.newUser.selectedTeams = this.user.teams.map(team => team.id);
    },
    uncheckAllTeams() {
      // Unselect all teams in the checkbox group
      this.newUser.selectedTeams = [];
    },
    resetNewUser() {
      this.newUser = {
        first_name: '',
        last_name: '',
        email: '',
        agency: this.user.team_agency,
        selectedTeams: []
      };
    },
    openChangePasswordModal(u) {
      this.userToChangePassword = u;
      this.changePasswordForm = { password: '', passwordConfirmation: '' };
      this.showChangePasswordModal = true;
    },
    resetChangePasswordForm() {
      this.userToChangePassword = null;
      this.changePasswordForm = { password: '', passwordConfirmation: '' };
      this.showChangePasswordModal = false;
    },
    submitChangePassword() {
      if (!this.userToChangePassword) return;
      const { password, passwordConfirmation } = this.changePasswordForm;
      if (!password || password.length < 8) {
        Vue.$toast.open({ message: 'Password must be at least 8 characters', type: 'error' });
        return;
      }
      if (password !== passwordConfirmation) {
        Vue.$toast.open({ message: 'Password and confirmation do not match', type: 'error' });
        return;
      }
      this.loadingChangePassword = true;
      this.$store
        .dispatch('user/changePassword', {
          user_id: this.userToChangePassword.id,
          password,
          password_confirmation: passwordConfirmation
        })
        .then(() => {
          this.loadingChangePassword = false;
          this.resetChangePasswordForm();
          Vue.$toast.open({ message: 'Password changed successfully', type: 'success' });
        })
        .catch((err) => {
          this.loadingChangePassword = false;
          const msg = err.response?.data?.message || err.response?.data?.errors?.password?.join?.(', ') || 'Failed to change password';
          Vue.$toast.open({ message: msg, type: 'error', duration: 5000 });
        });
    },
    addNewUser() {
      this.loadingUser = true
      this.$store
        .dispatch('user/addTeamUser', this.newUser)
        .then((res) => {
          this.user.teams = res.data.user.teams;
          this.user.created_team_users = res.data.user.created_team_users;
          this.loadingUser = false
          this.addUser = false;
          this.resetNewUser();
        })
        .catch((err) => {
          console.log(err);
          this.loadingUser = false
        })
    },
  },
}
</script>
<style>
.small-checkboxes .custom-control{
  padding-left: 1.25rem;
  margin-bottom:5px;
}
.small-checkboxes .custom-control-label::before,
.small-checkboxes .custom-control-label::after{
  width: 1.25rem; /* Adjust width */
  height: 1.25rem; /* Adjust height */
  top: 0; /* Adjust vertical position */
  left: -1.25rem; /* Adjust horizontal position */
  border-radius: 0.25rem; /* Optional: Adjust border radius for rounded checkboxes */
}

/* Adjust the custom checkbox input size (optional for more control) */
.small-checkboxes .custom-control-input {
  width: 1.25rem;  /* Adjust width */
  height: 1.25rem; /* Adjust height */
}

/* Adjust the font size of the label */
.small-checkboxes .custom-control-label {
  font-size: 0.875rem; /* Make the label smaller */
  padding-left: 5px;
}

/* Optional: Adjust the margin and padding to make it look more compact */
.small-checkboxes {
  margin-bottom: 0.5rem; /* Adjust as needed */
}
.scrollable-checkbox-group {
  max-height: 200px; /* Adjust height as needed */
  overflow-y: auto;
  border: 1px solid #ccc; /* Optional: Adds a border */
  padding: 5px;
}
</style>
